import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { usajiliApi } from '@services/api/usajili';
import { useUSAJILI } from '@hooks/useUSAJILI';
import { countyColor, formatCompact } from '@utils/usajiliFormat';
import { USAJILI_COUNTIES } from '@types';

/** Line chart — voters registered per day, one line per county. Fills its parent's height. */
export const USAJILITrendVotersTab: React.FC = () => {
  const { filters, refetchKey, metadata } = useUSAJILI();
  const t = useTheme();
  const phone = useMediaQuery(t.breakpoints.down('sm'));
  const axisTick = { fontSize: phone ? 10 : 12, fill: t.palette.text.secondary };
  const tipStyle = { fontSize: 12, background: t.palette.background.paper, border: `1px solid ${t.palette.divider}`, color: t.palette.text.primary };
  const allCounties = metadata?.counties || USAJILI_COUNTIES;
  const { data, isLoading, error } = useQuery({
    queryKey: ['usajili_trend_voters', filters, refetchKey],
    queryFn: () => usajiliApi.trendVoters(filters),
  });

  const chartData = useMemo(() => {
    const raw = data?.points || [];
    // Build {date: '2026-07-28', county1: v, county2: v, ...}[]
    const byDate = new Map<string, Record<string, number | string>>();
    for (const p of raw) {
      if (!byDate.has(p.date)) byDate.set(p.date, { date: p.date });
      const obj = byDate.get(p.date)!;
      obj[p.county] = p.voters_registered;
    }
    const dates = Array.from(byDate.keys()).sort();
    return dates.map((d) => byDate.get(d)!);
  }, [data]);

  // Only draw (and list in the legend) counties present under the active filters.
  const counties = useMemo(() => {
    const present = new Set((data?.points || []).map((p) => p.county));
    return allCounties.filter((c) => present.has(c));
  }, [data, allCounties]);

  const shortDate = (d: string) => {
    const t = new Date(d + 'T00:00:00');
    return isNaN(t.getTime()) ? d : t.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Typography sx={{ mb: 0.5, fontWeight: 700, fontSize: '0.8rem', letterSpacing: 0.3 }}>
        DAILY REGISTRATION OF VOTERS PER COUNTY
      </Typography>
      <Paper data-pdf="usajili-trend" sx={{ p: 1, flex: 1, minHeight: 0, width: '100%' }}>
        {isLoading ? (
          <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>
        ) : error ? (
          <Alert severity="error">{(error as any)?.error?.message || 'Failed to load Trend chart'}</Alert>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 4, right: phone ? 8 : 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.palette.divider} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                minTickGap={phone ? 12 : 20}
                height={phone ? 24 : 36}
                tick={axisTick}
                label={phone ? undefined : { value: 'Date', position: 'insideBottom', offset: 0, style: { fontWeight: 600, fontSize: 11, fill: t.palette.text.secondary } }}
              />
              <YAxis
                label={phone ? undefined : { value: 'No. Reg', angle: -90, position: 'insideLeft', offset: 10, style: { fontWeight: 600, fontSize: 11, fill: t.palette.text.secondary } }}
                tick={axisTick}
                tickFormatter={formatCompact}
                width={phone ? 36 : 62}
              />
              <Tooltip labelFormatter={(d) => shortDate(String(d))} contentStyle={tipStyle} />
              <Legend verticalAlign="top" iconSize={phone ? 8 : 10} wrapperStyle={{ fontSize: phone ? 10 : 11, lineHeight: '16px', paddingBottom: 4 }} />
              {counties.map((c) => (
                <Line
                  key={c}
                  type="monotone"
                  dataKey={c}
                  name={c}
                  stroke={countyColor(c)}
                  strokeWidth={1.75}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
};
